import { Injectable } from '@angular/core';
import { IProductBase, IProductCreationParams, IProductDeleteParams, IProductReservationBase, IProductReservationParams, Profility } from 'model/product.interface';
import {
  FlyProduct,
  FlySearchPayload,
  FlightItineraryLeg,
  FlightCancellationPolicy,
  FlightPricing,
  FlightFareOption,
  FlightFareVoidWindow,
  payloadUpdateFly,
  FlightProductReservation,
  FlightProductAdditionalInfoReserve,
  FlightsProductReserve,
  PaxlistFlightProductReserve,
} from 'model/fly.interface';
import { keyRandomUnique } from 'utils/helpers';
import { BaseProductService } from './base-product.service';
import * as moment from 'moment';

interface FlightRouteInfo {
  origin: string;
  destinationAirport: string;
  departureDate: string;
  returnDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class FlyProductService extends BaseProductService {

  private readonly CATEGORY_ID = 5;
  private readonly CATEGORY_NAME = 'FLIGHTS';

  createProduct(params: IProductCreationParams): IProductBase {

    const { item, payload, othersPerCategory } = params;

    const itemCopy = this.deepCopy(item);
    const searchPayload = this.getSearchPayload(payload);

    this.commonsValueProduct(itemCopy, othersPerCategory);
    this.setupFlightSpecificProperties(itemCopy, searchPayload, othersPerCategory);

    itemCopy.payload = payload;
    itemCopy.payloadUpdate = this._payloadUpdateFly(itemCopy, searchPayload, othersPerCategory);
    itemCopy.destination = this.itineraryService.getDestinationInfo({}, itemCopy);

    // getDestinationInfo() no tiene un "form" de vuelos del que sacar el
    // cityName (ese form es el de hoteles/tours), así que sin este fallback
    // queda undefined y el carrito descarta el grupo de ciudad entero por
    // no tener nombre. Usamos el aeropuerto de destino como texto mínimo.
    if (itemCopy.destination?.destination_info && !itemCopy.destination.destination_info.cityName) {
      itemCopy.destination.destination_info.cityName = itemCopy.destinationAirport;
    }

    return itemCopy as FlyProduct;
  }

  validateProduct(item: any): boolean {
    return !!(item?.bookParam && item?.itinerary?.length > 0 && item?.price);
  }

  private getSearchPayload(payload: any): FlySearchPayload {
    return Array.isArray(payload) ? payload[0] : payload;
  }

  private commonsValueProduct(item: any, othersData: any): void {
    item.id = item.bookParam || item.cacheId;
    item.categoryId = this.CATEGORY_ID;
    item.categoryName = this.CATEGORY_NAME;
    item.providerName = item.provider;
    item.nameAdapter = item.provider;
    item.currency = item.price?.currency;
    item.updateDate = new Date();
    item.searchId = othersData?.searchId;

    // Búsqueda "Por tramos": cada tramo elegido llega con su propio legTag
    // (mismo rol que firstWay en traslados) para poder mostrarlos agrupados
    // aunque sean items de itinerario independientes.
    if (othersData?.legTag) {
      item.flightGroupId = othersData.legTag.flightGroupId;
      item.direction = othersData.legTag.direction;
      item.legIndex = othersData.legTag.legIndex;
    }
  }

  private setupFlightSpecificProperties(item: any, searchPayload: FlySearchPayload, othersData: any): void {

    const legs = searchPayload?.legs || [];
    const route = this.getRouteInfo(searchPayload);

    item.legs = legs;
    item.origin = route.origin;
    item.destinationAirport = route.destinationAirport;
    item.departureDate = route.departureDate;
    item.returnDate = route.returnDate;

    item.adults = searchPayload?.others?.tours?.adults ?? 1;
    item.childrenAges = searchPayload?.others?.tours?.children ?? [];
    item.children = item.childrenAges.length;
    item.infants = 0;
    item.cabinClass = searchPayload?.cabinClass;
    item.market = searchPayload?.market;

    item.selectedOptions = this.getSelectedOptionsByLeg(item.itinerary);
    item.title = this.buildFlightTitle(item);
    item.productKey = keyRandomUnique();

    item.rateClassId = item.rateClass || item.fare?.rateClass;
    item.cancellationPolicies = item.cancellationPolicies || [];
    item.expiration_date = this.getExpirationDate(item.cancellationPolicies);
console.log(item)
    item.profility = this.getProfility(item);
    item.pricing = this.getPricing(item);
console.log(item.profility)
    item.netPrice = item.profility?.netPrice ?? item.price?.base ?? 0;
    item.netPriceFreeTax = item.pricing?.netPriceFreeTax ?? 0;
    item.taxPrice = item.price?.taxes ?? 0;
    item.totalPrice = item.pricing?.totalPriceProf ?? item.price?.total ?? 0;
    item.totalPriceProf = item.pricing?.totalPriceProf ?? 0;
    item.blickoins = item.pricing?.blickoins ?? 0;
    item.creditBlickoins = item.pricing?.creditBlickoins ?? 0;
    item.serviceExtra = [];
  }

  // El backend ya entrega la rentabilidad calculada en price.profility (misma
  // forma que usan tours/hoteles), así que no hace falta recalcularla acá.
  private getProfility(item: any): Profility {
    return item.price?.profility ?? {
      netPrice: item.price?.base ?? 0,
      netPriceBooking: item.price?.base ?? 0,
      netPriceCategory: item.price?.base ?? 0,
      netPriceProvider: item.price?.base ?? 0,
      percentage: 0,
      percentageBooking: 0,
      percentageCategory: 0,
      percentageProvider: 0,
      blickoins: 0,
      creditBlickoins: 0,
    };
  }

  private getPricing(item: any): FlightPricing {
    return {
      blickoins: item.price?.blickoins ?? 0,
      creditBlickoins: item.price?.creditBlickoins ?? 0,
      netPriceFreeTax: item.price?.netPriceFreeTax ?? 0,
      netPriceProf: item.price?.netPriceProf ?? 0,
      totalPriceFreeTax: item.price?.totalPriceFreeTax ?? 0,
      totalPriceProf: item.price?.totalPriceProf ?? 0,
      percentage: item.price?.percentage ?? 0,
      creditFactor: item.price?.creditFactor ?? 0,
      blickoinsFactor: item.price?.blickoinsFactor ?? 0,
    };
  }

  // La política de cancelación vigente es la primera del arreglo (igual que
  // el resto de las categorías, que toman cancellationPolicies[0]).
  private getExpirationDate(cancellationPolicies: FlightCancellationPolicy[]): string {
    return cancellationPolicies?.length > 0 ? cancellationPolicies[0].from : null;
  }

  private getRouteInfo(searchPayload: FlySearchPayload): FlightRouteInfo {
    const legs = searchPayload?.legs || [];
    return {
      origin: legs[0]?.origin,
      destinationAirport: legs[0]?.destination,
      departureDate: legs[0]?.date,
      returnDate: legs.length > 1 ? legs[legs.length - 1]?.date : null,
    };
  }

  private getSelectedOptionsByLeg(itinerary: FlightItineraryLeg[]): string[] {
    if (!itinerary || itinerary.length === 0) {
      return [];
    }
    return itinerary
      .map((leg: FlightItineraryLeg) => leg.options?.[0]?.optionId)
      .filter((optionId: string) => !!optionId);
  }

  // Título "origen - destino" del VIAJE (no del itinerario completo): usa
  // sólo el tramo de ida (itinerary[0]). Cruzar con el último segmento del
  // tramo de vuelta (itinerary[length-1]) da "EZE - EZE" en un ida y vuelta,
  // porque ese tramo vuelve a aterrizar en el origen.
  private buildFlightTitle(item: any): string {
    const outboundSegments = item.itinerary?.[0]?.options?.[0]?.segments;
    const firstSegment = outboundSegments?.[0];
    const lastSegment = outboundSegments?.[outboundSegments?.length - 1];

    if (!firstSegment || !lastSegment) {
      return `${item.origin || ''} - ${item.destinationAirport || ''}`;
    }

    return `${firstSegment.departure?.airport} - ${lastSegment.arrival?.airport}`;
  }

  private _payloadUpdateFly(
    item: any,
    searchPayload: FlySearchPayload,
    othersData: any,
    getFareOptions = false,
  ): payloadUpdateFly {

    return {
      products: [
        {
          providerId: item.providerId,
          productId: item.cacheId || item.bookParam,
          categoriesId: [this.CATEGORY_ID],
          market: searchPayload?.market,
          origin: item.origin,
          destination: item.destinationAirport,
          departureDate: item.departureDate,
          returnDate: item.returnDate,
          agencyId: othersData?.userService?._agencyID,
          flights: {
            bookingCode: item.bookParam,
            optionIds: item.selectedOptions,
            withAncillaries: false,
            getFareOptions,
          },
          creditCard: null,
        },
      ],
    };
  }

  /**
   * Payload para POST /syncProducts con flights.getFareOptions: true — trae
   * las familias tarifarias (Light/Standard/Full/Premium) disponibles para
   * el MISMO itinerario que ya trajo la búsqueda (misma combinación de
   * tramos/optionIds), no un producto nuevo. Se arma antes de crear el
   * producto, a partir de la oferta cruda que muestra el modal de detalle.
   */
  buildFareOptionsPayload(flightOffer: any, payload: any, othersPerCategory?: any): payloadUpdateFly {
    const searchPayload = this.getSearchPayload(payload);
    const route = this.getRouteInfo(searchPayload);

    const routeItem = {
      providerId: flightOffer?.providerId,
      cacheId: flightOffer?.cacheId,
      bookParam: flightOffer?.bookParam,
      origin: route.origin,
      destinationAirport: route.destinationAirport,
      departureDate: route.departureDate,
      returnDate: route.returnDate,
      selectedOptions: this.getSelectedOptionsByLeg(flightOffer?.itinerary),
    };

    return this._payloadUpdateFly(routeItem, searchPayload, othersPerCategory, true);
  }

  /**
   * Aplica la tarifa elegida en la pantalla de familias tarifarias (una de
   * fares[], de /syncProducts) sobre la oferta original, devolviendo un
   * flightOffer listo para pasar a createProduct(). Así el item que termina
   * en el itinerario ya reserva con el bookingCode/precio de la tarifa que
   * el usuario efectivamente eligió, y checkAvail más adelante sabe cuál fue.
   *
   * Las fares[] no traen "profility" propio (eso lo calcula el backend
   * recién al reservar) ni itinerary propio (ahora vive a nivel resultado de
   * /syncProducts, compartido por todas las tarifas de ese mismo vuelo) —
   * por eso se limpia profility acá (el fallback de getProfility()/
   * getPricing() ya contempla precio sin profility) y no se toca
   * item.itinerary, que ya trae el de la búsqueda original (mismo vuelo).
   */
  applySelectedFare(flightOffer: any, fare: FlightFareOption): any {
    if (!fare) {
      return flightOffer;
    }

    const merged = this.deepCopy(flightOffer);

    merged.bookParam = fare.bookingCode || merged.bookParam;
    merged.rateClass = fare.rateClass || merged.rateClass;
    merged.cancellationPolicies = fare.voidWindow
      ? [this.voidWindowToCancellationPolicy(fare.voidWindow)]
      : merged.cancellationPolicies;
    merged.passengers = fare.passengers?.length ? fare.passengers : merged.passengers;

    merged.price = {
      ...merged.price,
      total: fare.price?.total ?? merged.price?.total,
      base: fare.price?.base ?? merged.price?.base,
      taxes: fare.price?.taxes ?? merged.price?.taxes,
      currency: fare.price?.currency || merged.price?.currency,
      currencyLocal: fare.price?.currencyLocal || merged.price?.currencyLocal,
      totalLocal: fare.price?.totalLocal ?? merged.price?.totalLocal,
      profility: undefined,
      profilityFreeTax: undefined,
      netPriceProf: undefined,
      totalPriceProf: undefined,
      netPriceFreeTax: undefined,
      totalPriceFreeTax: undefined,
    };

    merged.fare = {
      ...merged.fare,
      validatingCarrier: fare.validatingCarrier || merged.fare?.validatingCarrier,
      cancelPolicy: fare.voidWindow?.code ?? merged.fare?.cancelPolicy,
      rateClass: fare.rateClass || merged.fare?.rateClass,
      brandName: fare.brandName || fare.cabin?.fareBasis || merged.fare?.brandName,
    };

    merged.selectedFare = {
      fareCode: fare.fareCode,
      fareId: fare.fareId,
      bookingCode: fare.bookingCode,
      recommendationID: fare.recommendationID,
    };

    return merged;
  }

  // voidWindow (nuevo, un único objeto) reemplaza a cancellationPolicies[]
  // (viejo, array) en la respuesta de /syncProducts — se adapta acá para no
  // tener que tocar todo lo que ya lee item.cancellationPolicies como array.
  private voidWindowToCancellationPolicy(voidWindow: FlightFareVoidWindow): FlightCancellationPolicy {
    return {
      amount: voidWindow.amount,
      from: voidWindow.until,
      currency: voidWindow.currency,
      descript_polecy: voidWindow.description,
      description: voidWindow.description,
      code: voidWindow.code || '',
    };
  }

  private deepCopy<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime()) as any;
    }
    if (Array.isArray(obj)) {
      return obj.map(entry => this.deepCopy(entry)) as any;
    }
    const copy = {} as any;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        copy[key] = this.deepCopy((obj as any)[key]);
      }
    }
    return copy;
  }

  // RESERVA

  createReserve(params: IProductReservationParams): IProductReservationBase {

    const { item, itineraryId, othersPerCategory } = params;
console.log(item);
    const productReservation: FlightProductReservation = {
      id: item.id,
      providerId: item.providerId,
      categoryId: this.CATEGORY_ID,
      itinerary_id: item.itinerary_id,
      productId: item.selectedFare?.fareId,
      categoriesId: [this.CATEGORY_ID],
      additionalInfo: this.setAdditionalInfo(item, othersPerCategory),
    };

    return productReservation as unknown as FlyProduct;
  }

  private setAdditionalInfo(item: any, othersData: any): FlightProductAdditionalInfoReserve {
    return {
      flights: [
        {
          bookingCode: item.bookParam,
          paxList: this.getPaxList(othersData.paxesInItems, othersData.itemId),
        },
      ],
    };
  }

  private getPaxList(paxesInItems: any, itemId: any, nationality?: string): PaxlistFlightProductReserve[] {
    const paxesAux: PaxlistFlightProductReserve[] = [];
    let paxes = [];

    if (paxesInItems !== undefined) {
      paxesInItems.forEach((subArray) => {
        if (Array.isArray(subArray)) {
          const filteredItems = subArray.filter((pax) => itemId == pax.itemId);
          paxes.push(...filteredItems);
        }
      });
    }

    if (paxes.length === 0) {
      paxes = paxesInItems || [];
    }

    for (let i = 0; i < paxes.length; i++) {
      paxesAux.push(this.getPaxData(paxes, i, nationality));
    }

    return paxesAux;
  }

  // Mismo criterio que InsuranceProductService.getPaxData(): si un dato
  // falta en el pasajero actual, se completa con el del primer pasajero
  // (el titular) antes que dejarlo vacío.
  getPaxData(pax: any, i: number, nationality?: string): PaxlistFlightProductReserve {
    const birth_date = moment(pax[i].birth_date, ['YYYY-DD-MM', 'DD-MM-YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY']).format('YYYY-MM-DD');

    return {
      gender: pax[i].gender || pax[0].gender,
      title: pax[i].title || pax[0].title,
      type: pax[i].type === 'AD' ? 'ADT' : 'CHD',
      firstName: pax[i].first_name?.trim() || pax[0].first_name?.trim() || '',
      lastName: pax[i].last_name?.trim() || pax[0].last_name?.trim() || '',
      birth_date: birth_date as unknown as Date,
      email: pax[i].email?.trim() || pax[0].email?.trim() || '',
      phone: pax[i].phone || pax[0].phone || '',
      address: pax[i].address?.trim() || pax[0].address?.trim() || '',
      country: pax[i].country_id || pax[0].country_id,
      city: pax[i].city || pax[0].city,
      state: pax[i].state_id || pax[0].state_id,
      nationality: nationality?.trim() || pax[i].country_id || pax[0].country_id || 'AR',
      documentType: pax[i].identificationType || pax[0].identificationType,
      documentNumber: pax[i].document_number || pax[0].document_number || '',
      // No hay ningún formulario en la app que capture vencimiento de
      // documento hoy — queda en null hasta que se agregue esa captura.
      documentExpiry: pax[i].documentExpiry || null,
      holder: i == 0 ? true : false,
    };
  }

  deleteProduct(params: IProductDeleteParams): any {
    throw new Error('Method not implemented.');
  }
}
